/* eslint-disable max-lines */
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { isAfter, isAfterNow } from '@modernloop/shared/datetime';
import { useFlag } from '@modernloop/shared/feature-flag';
import { MoreIcon, PlusIcon, TriangleDownIcon } from '@modernloop/shared/icons';
import { Box, Button, IconButton, Menu, MenuItem, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  ApplicationStatus,
  ExternalObjectSyncState,
  ScheduleContentActions_ApplicationStageFragment,
  ScheduleContentActions_InterviewsFragment,
  useRetryExternalObjectSyncMutation,
} from 'src/generated/mloop-graphql';

import { useOrgAtsService } from 'src/hooks/atsService';
import useHistory from 'src/hooks/useHistory';

import { ScheduleState } from 'src/types/schedule';

import getRescheduleFlowUrl from 'src/urls/getRescheduleFlowUrl';
import getUpdateFlowUrl from 'src/urls/getUpdateFlowUrl';

// eslint-disable-next-line modernloop/restrict-imports.cjs
import useCalendarPermissionData from 'src/views-new/Common/CalendarPermissionDialog/useCalendarPermissionData';
// eslint-disable-next-line modernloop/restrict-imports.cjs
import { ScheduleFlowType } from 'src/views-new/ScheduleFlow/Steps/Schedule/types';
// eslint-disable-next-line modernloop/restrict-imports.cjs
import { SidePanelManager } from 'src/views-new/sidepanel/common/SidePanelManager';

import AvailabilityCloseAlertModal from './AvailabilityCloseAlertModal';
import CalendarPermissionDialog from './CalendarPermissionDialog';
import CancelScheduleDialog from './CancelScheduleDialog';
import PushScheduleToAtsDialog from './PushScheduleToAtsDialog';
import SelfScheduledApplicationStageAlert from './SelfScheduleApplicationStageAlert';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by codegen
const RetryExternalObjectSyncMutationGQL = gql`
  mutation RetryExternalObjectSync($input: RetryExternalObjectSyncInput!) {
    retryExternalObjectSync(input: $input) {
      syncStatusIds
    }
  }
`;

type Props = {
  applicationStatus: ApplicationStatus;
};

type Fragments = {
  applicationStage: ScheduleContentActions_ApplicationStageFragment;
  interviews: ScheduleContentActions_InterviewsFragment[];
};

const ScheduleContentActions: FCWithFragments<Fragments, Props> = ({
  applicationStage,
  applicationStatus,
  interviews,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const atsService = useOrgAtsService();
  const atsName = atsService?.name || 'ATS';
  const showPushScheduleToAts = useFlag('user_show_push_schedule_to_ats');
  const atsExternalSyncTrackingEnabled = useFlag('org_async_ats_schedule_ops');
  const [showCancelScheduleDialog, setShowCancelScheduleDialog] = useState(false);
  const [showCalendarPermissionDialog, setShowCalendarPermissionDialog] = useState(false);
  const [showAvailabilityRequestAlert, setShowAvailabilityRequestAlert] = useState(false);
  const [showSelfScheduleAlert, setShowSelfScheduleAlert] = useState(false);
  const [showPushToAtsDialog, setShowPushToAtsDialog] = useState(false);
  const [scheduleFlowType, setScheduleFlowType] = useState<ScheduleFlowType>();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pushMenuOpen, setPushMenuOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const pushButtonRef = useRef<HTMLButtonElement>(null);

  const calendarDisplayData = useCalendarPermissionData({ applicationStageId: applicationStage.id });

  const checkPermission = useCallback(() => {
    if (!calendarDisplayData.hasPermission) {
      setShowCalendarPermissionDialog(true);
      return false;
    }
    return true;
  }, [calendarDisplayData.hasPermission]);

  const checkAvailabilityRequest = useCallback(
    (flowType: ScheduleFlowType) => {
      if (applicationStage.task?.availabilityRequests?.length && applicationStage.task?.availabilityRequests[0].id) {
        setScheduleFlowType(flowType);
        setShowAvailabilityRequestAlert(true);
        return true;
      }
      return false;
    },
    [applicationStage.task?.availabilityRequests]
  );

  const checkSelfSchedule = useCallback(
    (flowType: ScheduleFlowType) => {
      if (!applicationStage.selfScheduleOptionsId) {
        return false;
      }
      setScheduleFlowType(flowType);
      setShowSelfScheduleAlert(true);
      return true;
    },
    [applicationStage.selfScheduleOptionsId]
  );

  const handleUpdate = useCallback(() => {
    const updateUrl = getUpdateFlowUrl(applicationStage.id);
    history.push(updateUrl);
    SidePanelManager.closeSidePanel();
  }, [applicationStage.id, history]);

  const checkAndHandleUpdate = useCallback(() => {
    if (!checkPermission()) {
      return;
    }
    if (checkAvailabilityRequest(ScheduleFlowType.UPDATE)) {
      return;
    }
    if (checkSelfSchedule(ScheduleFlowType.UPDATE)) {
      return;
    }

    handleUpdate();
  }, [checkAvailabilityRequest, checkPermission, checkSelfSchedule, handleUpdate]);

  const handleReschedule = useCallback(() => {
    const rescheduleUrl = getRescheduleFlowUrl(applicationStage.id);
    history.push(rescheduleUrl);
    SidePanelManager.closeSidePanel();
  }, [applicationStage.id, history]);

  const checkAndHandleReschedule = useCallback(() => {
    if (!checkPermission()) {
      return;
    }
    if (checkAvailabilityRequest(ScheduleFlowType.RESCHEDULE)) {
      return;
    }
    if (checkSelfSchedule(ScheduleFlowType.RESCHEDULE)) {
      return;
    }

    handleReschedule();
  }, [checkAvailabilityRequest, checkPermission, checkSelfSchedule, handleReschedule]);

  const handleAddInterview = useCallback(() => {
    if (!checkPermission()) {
      return;
    }
    const updateUrl = getUpdateFlowUrl(applicationStage.id, { addNewInterview: true });
    history.push(updateUrl);
    SidePanelManager.closeSidePanel();
  }, [applicationStage.id, checkPermission, history]);

  const handleCancelSchedule = useCallback(() => {
    if (!checkPermission()) {
      return;
    }

    setShowCancelScheduleDialog(true);
  }, [checkPermission]);

  const handlePushToAts = useCallback(() => {
    setPushMenuOpen(false);
    setShowPushToAtsDialog(true);
  }, []);

  const [retryExternalObjectSync, { loading: retryingExternalObjectSync }] = useRetryExternalObjectSyncMutation({
    onCompleted: (data) => {
      const syncStatusIds = data.retryExternalObjectSync?.syncStatusIds ?? [];
      if (syncStatusIds.length > 0) {
        enqueueSnackbar(t('hiring-dashboard-schedule:schedule.contentActions.retryAtsSyncQueued', { atsName }), {
          variant: 'success',
        });
      } else {
        // Nothing was retryable (already synced, or an execution is in flight) — inform, not cheer.
        enqueueSnackbar(t('hiring-dashboard-schedule:schedule.contentActions.retryAtsSyncNothingQueued', { atsName }), {
          variant: 'info',
        });
      }
      setMenuOpen(false);
    },
    onError: () => {
      enqueueSnackbar(t('hiring-dashboard-schedule:schedule.contentActions.retryAtsSyncFailed', { atsName }), {
        variant: 'error',
      });
      setMenuOpen(false);
    },
  });

  const handleRetryAtsSync = useCallback(() => {
    if (!applicationStage.task?.id) {
      return;
    }
    retryExternalObjectSync({ variables: { input: { taskId: applicationStage.task.id } } });
  }, [applicationStage.task?.id, retryExternalObjectSync]);

  const hasFailedAtsSync = useMemo(
    () => interviews?.some((interview) => interview.atsSyncStatus?.state === ExternalObjectSyncState.Failed) ?? false,
    [interviews]
  );

  const showRetryAtsSync = atsExternalSyncTrackingEnabled && hasFailedAtsSync;

  const upcomingInterviews: ScheduleContentActions_InterviewsFragment[] = useMemo(
    () =>
      interviews
        ?.filter((schedule) => isAfterNow(schedule.endAt))
        ?.sort((a, b) => {
          if (!a || !b) {
            return 0;
          }
          if (isAfter(a.startAt, b.startAt)) {
            return 1;
          }
          return -1;
        }) ?? [],

    [interviews]
  );

  const scheduleState = useMemo(() => {
    if (upcomingInterviews.length === 0) {
      return ScheduleState.COMPLETED;
    }

    return ScheduleState.UPCOMING;
  }, [upcomingInterviews]);

  const actionButtons = useMemo(() => {
    if (applicationStatus === ApplicationStatus.Archived) {
      return null;
    }

    const addInterviewButton = !applicationStage.frozenAt && (
      <Button
        key="schedule-content-actions-add-interview-button"
        data-testid="schedule-content-actions-add-interview-button"
        size="small"
        startIcon={<PlusIcon />}
        onClick={handleAddInterview}
      >
        {t('hiring-dashboard-schedule:schedule.contentActions.addInterview')}
      </Button>
    );

    switch (scheduleState) {
      case ScheduleState.UPCOMING: {
        if (applicationStage.frozenAt) {
          return [
            <Button
              key="schedule-content-actions-cancel-button"
              data-testid="schedule-content-actions-cancel-button"
              size="small"
              onClick={handleCancelSchedule}
            >
              {t('hiring-dashboard-common:common.cancel')}
            </Button>,
          ];
        }
        return [
          <Button
            key="schedule-content-actions-update-button"
            data-testid="schedule-content-actions-update-button"
            size="small"
            onClick={checkAndHandleUpdate}
          >
            {t('hiring-dashboard-schedule:schedule.contentActions.update')}
          </Button>,
          <Button
            key="schedule-content-actions-reschedule-button"
            data-testid="schedule-content-actions-reschedule-button"
            size="small"
            onClick={checkAndHandleReschedule}
          >
            {t('hiring-dashboard-schedule:schedule.contentActions.reschedule')}
          </Button>,
          <Button
            key="schedule-content-actions-cancel-button"
            data-testid="schedule-content-actions-cancel-button"
            size="small"
            onClick={handleCancelSchedule}
          >
            {t('hiring-dashboard-common:common.cancel')}
          </Button>,
        ];
      }
      case ScheduleState.COMPLETED: {
        return [
          addInterviewButton,
          <Button
            key="schedule-content-actions-cancel-button"
            data-testid="schedule-content-actions-cancel-button"
            size="small"
            onClick={handleCancelSchedule}
          >
            {t('hiring-dashboard-schedule:schedule.contentActions.markCanceled')}
          </Button>,
        ];
      }
      default:
        return null;
    }
  }, [
    applicationStage.frozenAt,
    applicationStatus,
    checkAndHandleReschedule,
    checkAndHandleUpdate,
    handleAddInterview,
    handleCancelSchedule,
    scheduleState,
    t,
  ]);

  const canShowAtsActions = applicationStage.task?.id && applicationStatus === ApplicationStatus.Active;

  // Push is available for both upcoming and completed schedules so past schedules can be synced.
  const showPushButton = showPushScheduleToAts && canShowAtsActions;

  const showMoreMenu = showRetryAtsSync && scheduleState === ScheduleState.UPCOMING && canShowAtsActions;

  const showTrailingActions = showPushButton || showMoreMenu;

  return (
    <Stack direction="row" gap={1} px={2.5} py={1.5} alignItems="center">
      {actionButtons}
      {showTrailingActions && (
        <>
          <Box sx={{ flexGrow: 1 }} />
          {showPushButton && (
            <>
              <Button
                ref={pushButtonRef}
                size="small"
                endIcon={<TriangleDownIcon />}
                onClick={() => setPushMenuOpen(true)}
                data-testid="schedule-content-actions-push-button"
              >
                {t('hiring-dashboard-schedule:schedule.contentActions.push')}
              </Button>
              <Menu anchorEl={pushButtonRef.current} open={pushMenuOpen} onClose={() => setPushMenuOpen(false)}>
                <MenuItem onClick={handlePushToAts} data-testid="schedule-content-actions-push-to-ats">
                  {t('hiring-dashboard-schedule:schedule.pushToAts.pushTo', { atsName })}
                </MenuItem>
              </Menu>
            </>
          )}
          {showMoreMenu && (
            <>
              <IconButton
                ref={moreButtonRef}
                onClick={() => setMenuOpen(true)}
                size="small"
                data-testid="schedule-content-actions-more-button"
              >
                <MoreIcon />
              </IconButton>
              <Menu anchorEl={moreButtonRef.current} open={menuOpen} onClose={() => setMenuOpen(false)}>
                <MenuItem
                  disabled={retryingExternalObjectSync}
                  onClick={handleRetryAtsSync}
                  data-testid="schedule-content-actions-retry-ats-sync"
                >
                  {t('hiring-dashboard-schedule:schedule.contentActions.retryAtsSync', { atsName })}
                </MenuItem>
              </Menu>
            </>
          )}
        </>
      )}
      {showCancelScheduleDialog && (
        <CancelScheduleDialog
          applicationStage={applicationStage}
          interviews={interviews}
          onSuccess={() => setShowCancelScheduleDialog(false)}
          onError={() => setShowCancelScheduleDialog(false)}
          onClose={() => setShowCancelScheduleDialog(false)}
        />
      )}

      {showPushToAtsDialog && applicationStage.task?.id && (
        <PushScheduleToAtsDialog taskId={applicationStage.task.id} onClose={() => setShowPushToAtsDialog(false)} />
      )}

      {showCalendarPermissionDialog && (
        <CalendarPermissionDialog
          fullName={calendarDisplayData.scheduler?.fullName || ''}
          imageUrl={calendarDisplayData.scheduler?.internalProfilePictureUrl || ''}
          email={calendarDisplayData.scheduler?.email || ''}
          candidateCalendarLabel={calendarDisplayData.candidateCalendar}
          candidateId={calendarDisplayData.candidateId}
          candidateEventCalendarId={calendarDisplayData.candidateEventCalendarId}
          interviewerEventCalendarId={calendarDisplayData.interviewerEventCalendarId}
          candidateCalendarFound={calendarDisplayData.candidateCalendarFound}
          interviewerCalendarLabel={calendarDisplayData.interviewerCalendar}
          interviewerCalendarFound={calendarDisplayData.interviewerCalendarFound}
          onClose={() => setShowCalendarPermissionDialog(false)}
        />
      )}

      {showSelfScheduleAlert && (
        <SelfScheduledApplicationStageAlert
          onClose={() => setShowSelfScheduleAlert(false)}
          onContinue={() => {
            if (scheduleFlowType === ScheduleFlowType.UPDATE) {
              handleUpdate();
            } else if (scheduleFlowType === ScheduleFlowType.RESCHEDULE) {
              handleReschedule();
            }
            setScheduleFlowType(undefined);
          }}
        />
      )}

      {showAvailabilityRequestAlert && (
        <AvailabilityCloseAlertModal
          onClose={() => setShowAvailabilityRequestAlert(false)}
          onContinue={() => {
            if (scheduleFlowType === ScheduleFlowType.UPDATE) {
              handleUpdate();
            } else if (scheduleFlowType === ScheduleFlowType.RESCHEDULE) {
              handleReschedule();
            }
            setScheduleFlowType(undefined);
          }}
        />
      )}
    </Stack>
  );
};

ScheduleContentActions.fragments = {
  applicationStage: gql`
    ${CancelScheduleDialog.fragments.applicationStage}
    fragment ScheduleContentActions_applicationStage on ApplicationStage {
      id
      selfScheduleOptionsId
      frozenAt
      task {
        id
        availabilityRequests {
          id
        }
      }
      ...CancelScheduleDialog_applicationStage
    }
  `,
  interviews: gql`
    ${CancelScheduleDialog.fragments.interviews}
    fragment ScheduleContentActions_interviews on ApplicationStageInterview {
      id
      startAt
      endAt
      atsSyncStatus {
        id
        state
      }
      ...CancelScheduleDialog_interviews
    }
  `,
};

export default ScheduleContentActions;
